import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Smartphone, Share, Download, CheckCircle2, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";

type InstallPrompt = {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

export default function Install() {
  const [deferredPrompt, setDeferredPrompt] = useState<InstallPrompt | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && "standalone" in navigator);
    setIsIOS(iOS);

    // Detect if already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches ||
        ("standalone" in navigator && (navigator as any).standalone)) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as any);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;
    if (choiceResult.outcome === "accepted") {
      setDeferredPrompt(null);
      setIsInstalled(true);
    }
  };

  return (
    <>
      <Seo
        title="Install AleaEdge | Add to Home Screen"
        description="Install AleaEdge on your phone for instant access to daily AI sports picks and betting insights."
        path="/install"
      />
      <div className="container max-w-lg mx-auto px-4 py-12 text-center">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mb-6">
          <div className="mx-auto w-24 h-24 rounded-3xl bg-gradient-primary shadow-glow grid place-items-center mb-6">
            <Smartphone className="w-12 h-12 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-display font-bold mb-3">Install AleaEdge</h1>
          <p className="text-muted-foreground">
            Get instant access to daily AI sports picks right from your home screen — no app store required.
          </p>
        </div>

        {isInstalled ? (
          <div className="te-card p-8">
            <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Already Installed</h2>
            <p className="text-muted-foreground">You are using AleaEdge as an installed app. Enjoy!</p>
            <Button asChild className="mt-6 bg-gradient-primary text-primary-foreground">
              <Link to="/">Open Dashboard</Link>
            </Button>
          </div>
        ) : isIOS ? (
          <div className="te-card p-8 text-left space-y-6">
            <h2 className="text-lg font-semibold text-center">How to Install on iPhone</h2>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary grid place-items-center text-sm font-bold">1</div>
              <div>
                <p className="font-medium">Open Safari</p>
                <p className="text-sm text-muted-foreground">Make sure you are using Safari browser.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary grid place-items-center text-sm font-bold">2</div>
              <div>
                <p className="font-medium">Tap the Share button</p>
                <p className="text-sm text-muted-foreground">Look for the <Share className="inline w-4 h-4 mx-1" /> icon at the bottom of Safari.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary grid place-items-center text-sm font-bold">3</div>
              <div>
                <p className="font-medium">Select "Add to Home Screen"</p>
                <p className="text-sm text-muted-foreground">Scroll down and tap it, then tap "Add" in the top right.</p>
              </div>
            </div>
          </div>
        ) : deferredPrompt ? (
          <div className="te-card p-8">
            <Download className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Ready to Install</h2>
            <p className="text-muted-foreground mb-6">Add AleaEdge to your home screen for quick access.</p>
            <Button onClick={handleInstall} className="bg-gradient-primary text-primary-foreground hover:opacity-90">
              <Download className="w-4 h-4 mr-2" /> Install Now
            </Button>
          </div>
        ) : (
          <div className="te-card p-8">
            <Smartphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Open in Browser</h2>
            <p className="text-muted-foreground mb-6">
              To install, open this page in your phone's default browser (Chrome, Edge, or Samsung Internet), then use the browser menu to add to home screen.
            </p>
            <Button asChild variant="outline">
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        )}

        <div className="mt-10 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold te-grad-text">⚡</div>
            <p className="text-xs text-muted-foreground mt-1">Instant Access</p>
          </div>
          <div>
            <div className="text-2xl font-bold te-grad-text">🔄</div>
            <p className="text-xs text-muted-foreground mt-1">Auto Updates</p>
          </div>
          <div>
            <div className="text-2xl font-bold te-grad-text">📶</div>
            <p className="text-xs text-muted-foreground mt-1">Works Offline</p>
          </div>
        </div>
      </div>
    </>
  );
}
